import { nextTick } from 'vue'
import { gsap } from "gsap"

const DAMPING = 0.88
const STIFFNESS = 0.33
const INERTIA_SCALE = 0.03
const MAX_ANGULAR_VEL = 0.22
const GRAVITY = 0.5
const SHADOW_INTERVAL = 6
const OFFSCREEN_THRESHOLD = 1.2
const DEAD_ZONE = 2

export const activeDrags = []
let rafId = null
let globalFrame = 0
let winH = window.innerHeight

window.addEventListener("resize", () => {
    winH = window.innerHeight
})

const clamp = (v, min, max) => v < min ? min : v > max ? max : v
const snapHalf = v => (v * 2 | 0) * 0.5

const updateShadow = (el, angle, frame) => {
    if (frame % SHADOW_INTERVAL) return
    const s = Math.sin(angle)
    const c = Math.cos(angle)
    const ox = (s * 12) | 0
    const oy = Math.abs(c * 8) | 0
    el.style.boxShadow =
        `${ox}px ${oy + 4}px 16px -8px rgba(0,0,0,.2), ${(ox * .3) | 0}px ${((oy * .2 + 1) | 0)}px 2px rgba(0,0,0,.12)`
}

const render = (el, x, y, angle) => {
    el.style.transform = `translate3d(${x}px,${y}px,0) rotate(${angle}rad)`
}

export const cleanupDrag = (drag, dragState) => {
    if (!drag) return
    const i = activeDrags.indexOf(drag)
    if (i !== -1) activeDrags.splice(i, 1)
    drag.clone.remove()

    if (dragState?.clone === drag.clone) {
        dragState.clone = null
        dragState.item = null
    }
}

const rafLoop = () => {
    globalFrame++

    for (let i = activeDrags.length - 1; i >= 0; i--) {
        const d = activeDrags[i]
        const { clone, physics, falling } = d

        if (falling.active) {
            falling.velY += GRAVITY
            falling.currentX += falling.velX
            falling.currentY += falling.velY
            falling.velX *= 0.98

            if (falling.currentY > winH * 1.5 + clone.offsetHeight) {
                cleanupDrag(d, d.dragState)
                continue
            }

            if (falling.currentY <= winH * OFFSCREEN_THRESHOLD) {
                const target = falling.velX * INERTIA_SCALE
                physics.angularVel = physics.angularVel * 0.85 + target * 0.15
                physics.angularVel = clamp(
                    physics.angularVel * DAMPING - STIFFNESS * physics.angle,
                    -d.maxAngVel,
                    d.maxAngVel
                )
                physics.angularVel *= DAMPING
                physics.angle += physics.angularVel
            }

            render(
                clone,
                snapHalf(falling.currentX),
                snapHalf(falling.currentY),
                physics.angle
            )
            updateShadow(clone, physics.angle, globalFrame)

        } else {
            physics.angularVel = physics.angularVel * DAMPING - STIFFNESS * physics.angle
            physics.angle += physics.angularVel

            render(clone, d.x, d.y, physics.angle)
            updateShadow(clone, physics.angle, globalFrame)
        }
    }

    if (!activeDrags.length) {
        rafId = null
        return
    }

    rafId = requestAnimationFrame(rafLoop)
}

export const startDrag = (item, originalEl, event, dragState, isKeyboardDrag = false) => {
    event.preventDefault()

    const rect = originalEl.getBoundingClientRect()
    const clone = originalEl.cloneNode(true)

    const area = rect.width * rect.height
    const mass = Math.sqrt(area) / 50
    const maxAngVel = MAX_ANGULAR_VEL / Math.sqrt(mass)

    if (clone.classList.contains("hidden")) clone.classList.remove('hidden')
    clone.classList.add("drag-clone")

    Object.assign(clone.style, {
        position: "fixed",
        left: "0px",
        top: "0px",
        width: rect.width + "px",
        height: clone.classList.contains('autoheight') ? 'auto' : rect.height + "px",
        zIndex: 9999,
        pointerEvents: "none",
        transformOrigin: "top center",
        willChange: "transform"
    })

    let x = rect.left
    let y = rect.top

    clone.style.transform = `translate3d(${x}px,${y}px,0)`

    document.body.appendChild(clone)

    dragState.item = item
    dragState.clone = clone

    requestAnimationFrame(() => clone.classList.add("dragging"))

    const offsetX = event.clientX - rect.left
    const offsetY = event.clientY - rect.top

    const physics = { angle: 0, angularVel: 0 }
    const falling = { active: false, currentX: 0, currentY: 0, velX: 0, velY: 0 }

    let prevX = event.clientX
    let prevY = event.clientY
    let prevPrevX = prevX
    let prevPrevY = prevY
    let movement = 0

    const entry = { clone, physics, falling, cancelled: false, dragState, x, y, mass, maxAngVel }
    activeDrags.push(entry)

    const move = e => {
        const cx = e.clientX
        const cy = e.clientY

        x = snapHalf(cx - offsetX)
        y = snapHalf(cy - offsetY)

        entry.x = x
        entry.y = y

        const dx = cx - prevX

        movement = clamp(movement + (Math.abs(dx) > DEAD_ZONE ? 0.15 : -0.3), 0, 1)

        physics.angularVel = clamp(
            physics.angularVel * 0.6 + dx * INERTIA_SCALE * movement * 0.4 / mass,
            -maxAngVel,
            maxAngVel
        )

        prevPrevX = prevX
        prevPrevY = prevY
        prevX = cx
        prevY = cy
    }

    const pointerMove = e => {
        if (e.touches) {
            if (!e.touches[0]) return
            move(e.touches[0])
        } else {
            move(e)
        }
    }

    const stop = async () => {
        window.removeEventListener("mousemove", pointerMove)
        window.removeEventListener("touchmove", pointerMove)
        window.removeEventListener("mouseup", stop)
        window.removeEventListener("touchend", stop)
        window.removeEventListener("touchcancel", stop)

        if (isKeyboardDrag) window.removeEventListener("click", stop)

        if (entry.cancelled) {
            physics.angle = 0
            physics.angularVel = 0
            falling.active = true
            falling.velX = (prevX - prevPrevX) * 0.6
            falling.velY = (prevY - prevPrevY) * 0.6
            falling.currentX = x
            falling.currentY = y
            return
        }

        const target = document.elementFromPoint(prevX, prevY)
        const dropZoneEl = target?.closest(".drop-zone, .drop-zone-before")

        dragState.dropTarget = undefined
        await nextTick()
        dragState.dropTarget = dropZoneEl ?? null

        if (dropZoneEl && !dropZoneEl.classList.contains('forbidden')) {
            console.log(dropZoneEl)
            gsap.to(clone, {
                ease: "expo.out",
                duration: 0.2,
                scale: 0,
                onComplete: () => cleanupDrag(entry, dragState)
            })
            return
        }

        physics.angle = 0
        physics.angularVel = 0

        falling.active = true
        falling.velX = (prevX - prevPrevX) * 0.6
        falling.velY = (prevY - prevPrevY) * 0.6
        falling.currentX = x
        falling.currentY = y
    }

    window.addEventListener("mousemove", pointerMove, { passive: true })
    window.addEventListener("touchmove", pointerMove, { passive: true })
    window.addEventListener("mouseup", stop, { once: true })
    window.addEventListener("touchend", stop, { once: true })
    window.addEventListener("touchcancel", stop, { once: true })

    if (isKeyboardDrag) {
        setTimeout(() => window.addEventListener("click", stop, { once: true }), 0)
    }

    if (!rafId) rafId = requestAnimationFrame(rafLoop)

    return stop
}